import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, IconButton, Tab, Tabs } from "@mui/material";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";

import GetObjetivosAprendizajeV2 from "../../ObjetivosAprendizajeTP/GetObjetivosAprendizajeV2";
import { useProyecto } from "../../../data/ProyectoContext";

type AsignaturaTP = { id: string; nombre: string };

interface TabItem {
  asignaturaId: string;
  nombreAsignatura: string;
}

interface Props {
  resetKey: string;
  asignaturaSeleccionada: AsignaturaTP | null;
  nivelTPId: string;
  promptData: any;
  setPromptData: React.Dispatch<React.SetStateAction<any>>;
  setIsOAAdded?: React.Dispatch<React.SetStateAction<boolean>>;
}

function TabPanel(props: {
  children: React.ReactNode;
  value: number;
  index: number;
}) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`tp-tab-panel-${index}`}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function buildTabItem(a: AsignaturaTP): TabItem {
  return {
    asignaturaId: a.id,
    nombreAsignatura: a.nombre,
  };
}

function nextActiveIndex(current: number, deleteIndex: number, newLength: number) {
  if (newLength <= 0) return 0;
  if (current === deleteIndex) return deleteIndex === 0 ? 0 : deleteIndex - 1;
  if (current > deleteIndex) return current - 1;
  return current;
}

export default function TabsObjetivosTP({
  resetKey,
  asignaturaSeleccionada,
  nivelTPId,
  promptData,
  setPromptData,
  setIsOAAdded,
}: Props) {
  const [tabs, setTabs] = useState<TabItem[]>([]);
  const [value, setValue] = useState(0);

  const { setProyecto } = useProyecto();

  const asignaturaKey = useMemo(() => {
    if (!asignaturaSeleccionada?.id) return "";
    return asignaturaSeleccionada.id;
  }, [asignaturaSeleccionada]);


  useEffect(() => {
    setTabs([]);
    setValue(0);

    setPromptData((prev: any) => ({ ...prev, materia: "" }));
    if (setIsOAAdded) setIsOAAdded(false);

  
  }, [resetKey]);


  useEffect(() => {
    if (!asignaturaSeleccionada?.id || !asignaturaSeleccionada?.nombre) return;

    setTabs((prev) => {
      const existsIndex = prev.findIndex(
        (t) => t.asignaturaId === asignaturaSeleccionada.id
      );

      if (existsIndex >= 0) {
        setValue(existsIndex);
        setPromptData((prevPD: any) => ({
          ...prevPD,
          materia: prev[existsIndex]?.nombreAsignatura ?? asignaturaSeleccionada.nombre,
        }));
        return prev;
      }

      const nextTabs = [...prev, buildTabItem(asignaturaSeleccionada)];
      const nextIndex = nextTabs.length - 1;

      setValue(nextIndex);
      setPromptData((prevPD: any) => ({
        ...prevPD,
        materia: asignaturaSeleccionada.nombre,
      }));

      return nextTabs;
    });
  }, [asignaturaKey, asignaturaSeleccionada, setPromptData]);

  
  useEffect(() => {
    setProyecto((prev: any) => ({
      ...prev,
      materias: tabs.map((t) => t.nombreAsignatura),
    }));
  }, [tabs]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);

    const nextTab = tabs[newValue];
    setPromptData((prev: any) => ({
      ...prev,
      materia: nextTab?.nombreAsignatura ?? "",
    }));
  };

  const clearProyectoObjetivosForMateria = (asignaturaId: string) => {
  setProyecto((prev: any) => ({
    ...prev,
    objetivo_aprendizaje_tp: Array.isArray(prev?.objetivo_aprendizaje_tp)
      ? prev.objetivo_aprendizaje.filter((item: any) => item?.asignaturaId !== asignaturaId)
      : prev.objetivo_aprendizaje,
  }));
};

  const handleTabDelete = (deleteIndex: number) => {
    const tabToDelete = tabs[deleteIndex];
    if (!tabToDelete) return;

    const nextTabs = tabs.filter((_, i) => i !== deleteIndex);
    const nextIndex = nextActiveIndex(value, deleteIndex, nextTabs.length);

    setTabs(nextTabs);
    setValue(nextIndex);

    clearProyectoObjetivosForMateria(tabToDelete.asignaturaId);

    const nextMateria = nextTabs[nextIndex]?.nombreAsignatura ?? "";
    setPromptData((prev: any) => ({ ...prev, materia: nextMateria }));

    if (nextTabs.length === 0 && setIsOAAdded) {
      setIsOAAdded(false);
    }
  };

  return (
    <Box>
      <Tabs
        value={value}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Técnico profesional por asignatura"
        sx={{
          "& .MuiTabs-indicator": {
            backgroundColor: "#336B87",
            height: 3,
            width: "70%",
            borderRadius: 2,
          },
        }}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={tab.asignaturaId}
            id={`tp-tab-${index}`}
            aria-controls={`tp-tab-panel-${index}`}
            disableRipple
            label={
              <Grid
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  backgroundColor: "rgba(250, 219, 151, 1)",
                  color: "#336B87",
                  fontFamily: "Montserrat",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  pl: 3,
                  pr: 3,
                  pt: 2,
                  pb: 2,
                  borderRadius: "10px",
                }}
              >
                {tab.nombreAsignatura}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTabDelete(index);
                  }}
                  sx={{ ml: 1, color: "#336B87" }}
                >
                  <DeleteForeverRoundedIcon />
                </IconButton>
              </Grid>
            }
          />
        ))}
      </Tabs>

      {tabs[value] && (
        <TabPanel key={tabs[value].asignaturaId} value={value} index={value} >
          <GetObjetivosAprendizajeV2
            nivelId={nivelTPId}
            asignaturaId={tabs[value].asignaturaId}
            materiaNombre={tabs[value].nombreAsignatura} 
            promptData={promptData}
            setPromptData={setPromptData}
          />
        </TabPanel>
      )}
    </Box>
  );
}
